import { useMutation } from '@apollo/client';
import {
  Button,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  Modal,
  useDisclosure,
  ModalBody,
  Text,
  Box,
  Grid,
  Icon,
  Tab,
  Flex,
  TabList,
  Tabs,
  TabPanel,
  TabPanels,
} from '@chakra-ui/react';
import { FieldArray, Form, Formik, useFormikContext } from 'formik';
import TimeRangePicker from '@wojtekmaj/react-timerange-picker';
import { UPDATE_OPENING_HOURS, UPDATE_NA1ADL_STATUS } from '../query';
import React, { useContext } from 'react';
import { get } from 'lodash';
import moment, { Moment } from 'moment';
import { getMomentFromTime, getTimeFromMoment } from '../openingHours';
import SpinnerContext from '../../../contexts/SpinnerContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import UserContext from '../../../contexts/UserContext';
import { SingleCheckbox } from '../../../components/Checkbox';
import { XCircle } from '@phosphor-icons/react';

const weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const weekdayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
type Day = (typeof DAYS)[number];

const WeekdayOpeningHours = ({ weekday, weekdayKey, isCustom = false }: { weekday?: string; weekdayKey: string; isCustom?: boolean }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext<any>();
  if (!values[weekdayKey]) return null;

  return (
    <>
      <Box key={weekdayKey} mb="2">
        <Text fontWeight={500}>{weekday}</Text>
        <FieldArray name={weekdayKey}>
          {({ remove, push }) => {
            return (
              <React.Fragment>
                {values[weekdayKey]?.map((times: { from: string; to: string }, index: number) => {
                  const hasFromError = get(errors, `${weekdayKey}.${index}.from`, false);
                  const hasFromTouched = get(touched, `${weekdayKey}.${index}.from`, false);
                  const hasToError = get(errors, `${weekdayKey}.${index}.to`, false);
                  const hasToTouched = get(touched, `${weekdayKey}.${index}.to`, false);

                  const value = [times.from ? moment(times.from).format('HH:mm') : '', times.to ? moment(times.to).format('HH:mm') : ''];

                  const handleChange = (value) => {
                    if (value[0]) {
                      setFieldValue(`${weekdayKey}.${index}.from`, moment(value[0], 'HH:mm'));
                    }
                    if (value[1]) {
                      setFieldValue(`${weekdayKey}.${index}.to`, moment(value[1], 'HH:mm'));
                    }
                  };

                  return (
                    <Box key={`${weekdayKey}.${index}`}>
                      <Grid templateColumns="1fr 16px" mb="4" alignItems="center" gridGap="2">
                        <TimeRangePicker clearIcon={null} onChange={handleChange} value={value} disableClock={true} locale="de-DE" />
                        {index !== 0 || isCustom ? (
                          <XCircle onClick={() => remove(index)} color="red" style={{ cursor: 'pointer' }} />
                        ) : null}
                      </Grid>
                      {(hasFromError || hasToError) && hasFromTouched && hasToTouched ? (
                        <Text color="red" fontSize="sm">
                          Du musst eine Start- und Endzeit angeben.
                        </Text>
                      ) : null}
                    </Box>
                  );
                })}
                <Button
                  isDisabled={
                    !values[weekdayKey][values[weekdayKey].length - 1]?.from || !values[weekdayKey][values[weekdayKey].length - 1]?.to
                  }
                  mb="3"
                  size="xs"
                  variant="outline"
                  onClick={() =>
                    push({
                      from: '',
                      to: '',
                    })
                  }
                >
                  Zeit hinzufügen
                </Button>
              </React.Fragment>
            );
          }}
        </FieldArray>
      </Box>
    </>
  );
};

const CustomWeekdayOpeningHours = () => {
  return (
    <>
      {weekdayKeys.map((key, index) => (
        <WeekdayOpeningHours weekday={weekdays[index]} weekdayKey={key} isCustom={true} />
      ))}
    </>
  );
};

interface FormValues {
  na1adlActive: boolean;
  type: 'WEEKDAY' | 'CUSTOM';
  weekdays: { from: Moment; to: Moment }[];
  monday: { from: Moment; to: Moment }[];
  tuesday: { from: Moment; to: Moment }[];
  wednesday: { from: Moment; to: Moment }[];
  thursday: { from: Moment; to: Moment }[];
  friday: { from: Moment; to: Moment }[];
  saturday: { from: Moment; to: Moment }[];
  sunday: { from: Moment; to: Moment }[];
}

type FormTimeRange = { from: Moment; to: Moment };
type TimeRange = { from: number; to: number };

const serializeDay = (ranges: FormTimeRange[]): TimeRange[] | null =>
  ranges.length ? ranges.map(({ from, to }) => ({ from: getTimeFromMoment(from), to: getTimeFromMoment(to) })) : null;

export const UpdateOpeningHours = ({ dropPointId, openingHours, na1adlActive, refetch }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useContext(UserContext);
  const { toggleSpinner } = useContext(SpinnerContext);
  const toast = useCustomToast();
  const [updateOpeningHours] = useMutation(UPDATE_OPENING_HOURS);
  const [updateNa1adlStatus] = useMutation(UPDATE_NA1ADL_STATUS);

  const initialValues = {
    na1adlActive: typeof na1adlActive === 'boolean' ? na1adlActive : true,
    type: openingHours?.type || 'WEEKDAY',
    weekdays:
      openingHours?.type === 'WEEKDAY' && openingHours?.monday
        ? openingHours.monday.map((time) => ({
            from: getMomentFromTime(time.from),
            to: getMomentFromTime(time.to),
          }))
        : [],
    monday: openingHours?.monday
      ? openingHours.monday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
    tuesday: openingHours?.tuesday
      ? openingHours.tuesday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
    wednesday: openingHours?.wednesday
      ? openingHours.wednesday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
    thursday: openingHours?.thursday
      ? openingHours.thursday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
    friday: openingHours?.friday
      ? openingHours.friday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
    saturday: openingHours?.saturday
      ? openingHours.saturday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
    sunday: openingHours?.sunday
      ? openingHours.sunday.map((time) => ({
          from: getMomentFromTime(time.from),
          to: getMomentFromTime(time.to),
        }))
      : [],
  } as FormValues;

  return (
    <>
      <Button size="xs" colorScheme="brand" onClick={onOpen}>
        Öffnungszeiten ändern
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Öffnungszeiten ändern</ModalHeader>
          <ModalBody>
            <Formik
              initialValues={initialValues}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  toggleSpinner(true);
                  const { na1adlActive: initialNa1adlActive, ...restInitialValues } = initialValues;
                  const { na1adlActive, ...restValues } = values;

                  if (na1adlActive !== initialNa1adlActive) {
                    await updateNa1adlStatus({
                      variables: {
                        dropPointId,
                        userId: user.id,
                        na1adlActive: na1adlActive,
                      },
                    });
                  }

                  if (JSON.stringify(restInitialValues) !== JSON.stringify(restValues)) {
                    let times = {};
                    if (values.type === 'WEEKDAY') {
                      times = Object.fromEntries(
                        WEEKDAYS.map((day) => {
                          const serialized = serializeDay(values.weekdays);
                          return [day, serialized ? { create: serialized } : null];
                        })
                      ) as Record<Day, { create: TimeRange[] }>;
                    }
                    if (values.type === 'CUSTOM') {
                      times = Object.fromEntries(
                        DAYS.map((day) => {
                          const serialized = serializeDay(values[day as Day]);
                          return [day, serialized ? { create: serialized } : null];
                        })
                      ) as Record<Day, { create: TimeRange[] }>;
                    }

                    await updateOpeningHours({
                      variables: {
                        dropPointId,
                        userId: user.id,
                        openingHours: {
                          type: values.type,
                          ...times,
                        },
                      },
                    });
                  }

                  await refetch();

                  toast({
                    title: 'Erfolg!',
                    description: 'Die Öffnungszeiten wurden gespeichert.',
                    status: 'success',
                  });
                  onClose();
                } catch (error) {
                  console.log(error);
                  toast({
                    description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                    status: 'error',
                  });
                } finally {
                  setSubmitting(false);
                  toggleSpinner(false);
                }
              }}
            >
              {({ setFieldValue }) => (
                <Form>
                  <Tabs
                    defaultIndex={openingHours?.type === 'CUSTOM' ? 1 : 0}
                    onChange={(index) => setFieldValue('type', index === 0 ? 'WEEKDAY' : 'CUSTOM')}
                  >
                    <TabList>
                      <Tab>Alle Tage</Tab>
                      <Tab>Individuelle Tage</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel px="0">
                        <WeekdayOpeningHours weekday="" weekdayKey="weekdays" />
                      </TabPanel>
                      <TabPanel px="0">
                        <CustomWeekdayOpeningHours />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                  <SingleCheckbox
                    name="na1adlActive"
                    label="Ja, der Access Point möchte flexibel mehr verdienen. UPS wird dafür auch außerhalb seiner
                    Zeiten klingeln. Die Anwesenheit garantiert der Access Point allerdings nur während seiner
                    Öffnungszeiten."
                  />
                  <Flex justifyContent="flex-end" gap="2" mb="2">
                    <Button size="sm" colorScheme="red" type="button" variant="ghost" onClick={onClose}>
                      Abbrechen
                    </Button>
                    <Button size="sm" colorScheme="brand" type="submit">
                      Speichern
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
